import React, { useState, useEffect, useCallback } from "react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import LandingNav from "../../components/LandingNav";

// ── Data ─────────────────────────────────────────────────────────────────

const ASSET_BASE = `${process.env.REACT_APP_UPLOADED_URL}/realestate/landing`;

const HERO_SLIDES = [
  {
    image: `${ASSET_BASE}/product-hero-1.jpg`,
    heading: "One of a kind skywalk",
    subheading: "Aerial views of sites like never before",
  },
  {
    image: `${ASSET_BASE}/product-hero-2.jpg`,
    heading: "Drive view technology",
    subheading: "View the path to your site",
  },
  {
    image: `${ASSET_BASE}/product-hero-3.webp`,
    heading: "Digital twins",
    subheading: "To scale digital twins",
  },
];

const AUDIENCE_CARDS = [
  {
    icon: `${ASSET_BASE}/audience-builders.png`,
    title: "For Builders",
    description:
      "We give you the full flexibility to list as many properties in your first subscription year.",
  },
  {
    icon: `${ASSET_BASE}/audience-agents.png`,
    title: "For Agents",
    description:
      "As a promotion offer we are willing to take the scanning cost provided the property is qualified for global listing.",
  },
  {
    icon: `${ASSET_BASE}/audience-businesses.png`,
    title: "For Businesses",
    description:
      "Enable your clients to experience properties remotely, as if it was real and convert them faster.",
  },
  {
    icon: `${ASSET_BASE}/audience-our-efforts.png`,
    title: "Our Efforts",
    description:
      "We do everything possible for you to get what you need. We do the scans and the backend for you.",
  },
];

const FEATURE_SECTIONS = [
  {
    title: "For Builders",
    description:
      "We give you the full flexibility to list as many properties in your first subscription year. Showcase developments before they're built and accelerate pre-sales with immersive virtual walkthroughs.",
    image: `${ASSET_BASE}/feature-builders.jpg`,
    imagePosition: "left" as const,
  },
  {
    title: "For Agents",
    description:
      "As a promotion offer we are willing to take the scanning cost provided the property is qualified for global listing. Empower your agents with cutting-edge VR tours to close deals faster.",
    image: `${ASSET_BASE}/feature-agents.jpg`,
    imagePosition: "right" as const,
  },
  {
    title: "For Businesses",
    description:
      "Enable your clients to experience properties remotely, as if it was real and convert them faster. Turn commercial spaces into interactive digital twins accessible from anywhere.",
    image: `${ASSET_BASE}/feature-businesses.jpg`,
    imagePosition: "left" as const,
  },
];

// ── Hero Carousel ────────────────────────────────────────────────────────

const HeroCarousel: React.FC = () => {
  const [current, setCurrent] = useState(0);

  const next = useCallback(
    () => setCurrent((i) => (i + 1) % HERO_SLIDES.length),
    [],
  );
  const prev = () =>
    setCurrent((i) => (i === 0 ? HERO_SLIDES.length - 1 : i - 1));

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <div className="relative w-full h-[70vh] min-h-[500px] overflow-hidden">
      {HERO_SLIDES.map((slide, i) => (
        <img
          key={i}
          src={slide.image}
          alt={slide.heading}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            i === current ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}

      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/60" />

      {/* Slide caption */}
      <div className="absolute inset-0 z-[5] flex flex-col items-center justify-center text-center px-6">
        <h1
          className="text-white text-4xl sm:text-5xl md:text-6xl font-bold italic mb-4"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {HERO_SLIDES[current].heading}
        </h1>
        <p
          className="text-white/85 text-base sm:text-lg md:text-xl max-w-2xl"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          {HERO_SLIDES[current].subheading}
        </p>
      </div>

      {/* Arrows */}
      <button
        onClick={prev}
        aria-label="Previous slide"
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white/70 hover:text-white transition-colors"
      >
        <ArrowBackIosNewIcon sx={{ fontSize: 28 }} />
      </button>
      <button
        onClick={next}
        aria-label="Next slide"
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white/70 hover:text-white transition-colors"
      >
        <ArrowForwardIosIcon sx={{ fontSize: 28 }} />
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`w-2.5 h-2.5 rounded-full transition-colors ${
              i === current ? "bg-white" : "bg-white/40"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// ── Main Page ────────────────────────────────────────────────────────────

export const ProductPage: React.FC = () => {
  return (
    <div
      className="h-full overflow-y-auto bg-[#1a1a1a]"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <LandingNav active="product" />

      <HeroCarousel />

      {/* ── ANYTIME ANYWHERE Section ──────────────────────────── */}
      <section className="bg-[#1a1a1a] py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-6 sm:px-12">
          <h2
            className="text-white text-3xl sm:text-4xl md:text-5xl font-bold italic text-center mb-14"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            ANYTIME ANYWHERE
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6">
            {AUDIENCE_CARDS.map((card, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                <img
                  src={card.icon}
                  alt={card.title}
                  className="w-20 h-20 object-contain mb-4"
                />
                <h3
                  className="text-white text-base font-semibold mb-2"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {card.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  {card.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Feature Sections ──────────────────────────────────── */}
      <section className="bg-[#1a1a1a]">
        {FEATURE_SECTIONS.map((section, i) => (
          <div
            key={i}
            className="max-w-6xl mx-auto px-6 sm:px-12 py-12 md:py-16"
          >
            <div
              className={`flex flex-col ${
                section.imagePosition === "left"
                  ? "md:flex-row"
                  : "md:flex-row-reverse"
              } gap-8 md:gap-12 items-center`}
            >
              <div className="w-full md:w-1/2">
                <img
                  src={section.image}
                  alt={section.title}
                  className="w-full h-64 sm:h-80 object-cover rounded-lg"
                />
              </div>

              <div className="w-full md:w-1/2">
                <h3
                  className="text-white text-2xl sm:text-3xl font-bold mb-4"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {section.title}
                </h3>
                <p className="text-white/70 text-sm sm:text-base leading-relaxed mb-6">
                  {section.description}
                </p>
                <a
                  href="/login"
                  className="inline-block px-8 py-3 rounded-md text-sm font-medium text-white border border-[#b7aca2] hover:bg-[#b7aca2] hover:text-[#1a1a1a] transition-colors"
                >
                  Learn more
                </a>
              </div>
            </div>

            {i < FEATURE_SECTIONS.length - 1 && (
              <div className="border-t border-white/10 mt-12 md:mt-16" />
            )}
          </div>
        ))}
      </section>

      {/* ── Creator Board ─────────────────────────────────────── */}
      <section className="bg-[#111] py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center">
          <p
            className="text-[#b7aca2] text-sm uppercase tracking-[0.3em] mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Creator Board
          </p>
          <h2
            className="text-white text-3xl sm:text-4xl md:text-5xl font-bold italic mb-6"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Get the new power of real estate with you.
          </h2>
          <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            Blaze through the virtual reality world of real estate with your
            client. As of this subscription, 2X everything in your real estate
            business.
          </p>
          <a
            href="/pricing"
            className="inline-block px-10 py-3 rounded-md text-sm font-semibold text-[#1a1a1a] bg-[#b7aca2] hover:bg-white transition-colors"
          >
            See pricing
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#1c2228] py-10 px-6">
        <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <img src="/images/logo.svg" alt="OpenSky" className="h-10 w-auto" />
          <div className="flex gap-6 text-sm text-white/60">
            <a href="/privacy_policy" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="/terms_and_conditions" className="hover:text-white transition-colors">Terms & Conditions</a>
          </div>
          <p className="text-white/60 text-xs">© {new Date().getFullYear()}, OpenSky VR RE Platform</p>
        </div>
      </footer>
    </div>
  );
};

export default ProductPage;
